import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./CCTVCommandCenter.module.scss";

const CAMERA_FEEDS = [
	{
		id: "front-gate",
		name: "Front gate",
		zone: "Access",
		location: "Main entrance",
		status: "Live",
		quality: "1080p",
		signal: "Strong",
		accent: "#3155a6",
		scene: "entrance",
		activity: "Arrivals",
		lastMotion: "2 min ago",
	},
	{
		id: "classroom-a",
		name: "Classroom A",
		zone: "Rooms",
		location: "Kindergarten",
		status: "Live",
		quality: "1080p",
		signal: "Strong",
		accent: "#14845f",
		scene: "classroom",
		activity: "Circle time",
		lastMotion: "Now",
	},
	{
		id: "play-area",
		name: "Play area",
		zone: "Outdoor",
		location: "South yard",
		status: "Live",
		quality: "720p",
		signal: "Good",
		accent: "#b7791f",
		scene: "play",
		activity: "Outdoor play",
		lastMotion: "Now",
	},
	{
		id: "nap-room",
		name: "Nap room",
		zone: "Rooms",
		location: "Infant room",
		status: "Live",
		quality: "1080p",
		signal: "Strong",
		accent: "#6d5bd0",
		scene: "quiet",
		activity: "Quiet mode",
		lastMotion: "8 min ago",
	},
	{
		id: "corridor-one",
		name: "Main corridor",
		zone: "Common",
		location: "Ground floor",
		status: "Live",
		quality: "1080p",
		signal: "Strong",
		accent: "#0f766e",
		scene: "corridor",
		activity: "Clear",
		lastMotion: "4 min ago",
	},
	{
		id: "meal-room",
		name: "Meal room",
		zone: "Common",
		location: "Dining area",
		status: "Live",
		quality: "720p",
		signal: "Good",
		accent: "#c05621",
		scene: "meal",
		activity: "Lunch setup",
		lastMotion: "Now",
	},
	{
		id: "reception",
		name: "Reception",
		zone: "Access",
		location: "Admin desk",
		status: "Live",
		quality: "1080p",
		signal: "Strong",
		accent: "#2563eb",
		scene: "reception",
		activity: "Visitor desk",
		lastMotion: "1 min ago",
	},
	{
		id: "parking",
		name: "Parking",
		zone: "Outdoor",
		location: "North side",
		status: "Live",
		quality: "720p",
		signal: "Good",
		accent: "#475569",
		scene: "parking",
		activity: "Vehicle area",
		lastMotion: "6 min ago",
	},
];

const ZONES = ["All", "Access", "Rooms", "Common", "Outdoor"];

function formatTime(date) {
	return date.toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	});
}

function FeedVisual({ feed, large, t }) {
	return (
		<div
			className={`${styles.feedVisual} ${large ? styles.feedVisualLarge : ""}`}
			data-scene={feed.scene}
			style={{ "--feed-accent": feed.accent }}
		>
			<div className={styles.cameraFrame}>
				<div className={styles.videoNoise} />
				<div className={styles.sceneGrid}>
					<span />
					<span />
					<span />
					<span />
				</div>
				<div className={styles.sceneDepth}>
					<span />
					<span />
					<span />
				</div>
				<div className={styles.subjects}>
					<span />
					<span />
					<span />
				</div>
				<div className={styles.scanLine} />
			</div>
			<div className={styles.feedOverlayTop}>
				<span className={styles.liveBadge}>
					<span className={styles.liveDot} />
					{t(feed.status)}
				</span>
				<span>{feed.quality}</span>
			</div>
			<div className={styles.feedOverlayBottom}>
				<span>{t(feed.location)}</span>
				<span>{t(feed.activity)}</span>
			</div>
		</div>
	);
}

function FeedCard({ feed, onSelect, t }) {
	return (
		<button
			type="button"
			className={styles.feedCard}
			onClick={() => onSelect(feed.id)}
			aria-label={t("Open camera feed", { name: t(feed.name) })}
		>
			<FeedVisual feed={feed} t={t} />
			<div className={styles.feedInfo}>
				<div>
					<h3>{t(feed.name)}</h3>
					<p>{t(feed.zone)}</p>
				</div>
				<div className={styles.feedMeta}>
					<span className="material-icons">fiber_manual_record</span>
					{t(feed.signal)}
				</div>
			</div>
		</button>
	);
}

function CCTVCommandCenter({ isOpen, onClose }) {
	const { t } = useTranslation();
	const [selectedFeedId, setSelectedFeedId] = useState(null);
	const [activeZone, setActiveZone] = useState("All");
	const [query, setQuery] = useState("");
	const [now, setNow] = useState(new Date());
	const [density, setDensity] = useState("comfortable");

	useEffect(() => {
		if (!isOpen) return undefined;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				if (selectedFeedId) {
					setSelectedFeedId(null);
					return;
				}
				onClose();
			}
		};

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isOpen, onClose, selectedFeedId]);

	useEffect(() => {
		if (!isOpen) return undefined;
		const timer = window.setInterval(() => setNow(new Date()), 1000);
		return () => window.clearInterval(timer);
	}, [isOpen]);

	const visibleFeeds = useMemo(() => {
		const normalizedQuery = query.trim().toLowerCase();

		return CAMERA_FEEDS.filter((feed) => {
			const matchesZone = activeZone === "All" || feed.zone === activeZone;
			const matchesQuery =
				!normalizedQuery ||
				feed.name.toLowerCase().includes(normalizedQuery) ||
				feed.location.toLowerCase().includes(normalizedQuery) ||
				feed.zone.toLowerCase().includes(normalizedQuery);

			return matchesZone && matchesQuery;
		});
	}, [activeZone, query]);

	const selectedFeed = CAMERA_FEEDS.find((feed) => feed.id === selectedFeedId);
	const secondaryFeeds = CAMERA_FEEDS.filter((feed) => feed.id !== selectedFeedId);
	const onlineFeeds = CAMERA_FEEDS.filter((feed) => feed.status === "Live").length;

	useEffect(() => {
		if (isOpen) {
			setSelectedFeedId(null);
		}
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div className={styles.overlay} role="dialog" aria-modal="true">
			<header className={styles.commandHeader}>
				<div className={styles.titleBlock}>
					<div className={styles.iconTile}>
						<span className="material-icons">videocam</span>
					</div>
					<div>
						<span className={styles.eyebrow}>{t("Safety monitor")}</span>
						<h2>{t("CCTV command center")}</h2>
					</div>
				</div>

				<div className={styles.headerStats} aria-label={t("Camera status")}>
					<div>
						<strong>{CAMERA_FEEDS.length}</strong>
						<span>{t("Cameras")}</span>
					</div>
					<div>
						<strong>{onlineFeeds}</strong>
						<span>{t("Online")}</span>
					</div>
					<div>
						<strong>0</strong>
						<span>{t("Alerts")}</span>
					</div>
					<div>
						<strong>{formatTime(now)}</strong>
						<span>{t("Live time")}</span>
					</div>
				</div>

				<div className={styles.headerActions}>
					<button
						type="button"
						className={styles.headerButton}
						onClick={() => setSelectedFeedId(null)}
						disabled={!selectedFeedId}
						aria-label={t("Show all CCTV feeds")}
						title={t("Show all feeds")}
					>
						<span className="material-icons">grid_view</span>
					</button>
					<button
						type="button"
						className={styles.closeButton}
						onClick={onClose}
						aria-label={t("Close CCTV command center")}
						title={t("Close")}
					>
						<span className="material-icons">close</span>
					</button>
				</div>
			</header>

			<section className={styles.toolbar} aria-label={t("Camera filters")}>
				<label className={styles.searchBox}>
					<span className="material-icons">search</span>
					<input
						type="search"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder={t("Search camera, room, or zone")}
					/>
				</label>

				<div className={styles.zoneTabs}>
					{ZONES.map((zone) => (
						<button
							key={zone}
							type="button"
							className={activeZone === zone ? styles.activeZone : ""}
							onClick={() => {
								setActiveZone(zone);
								setSelectedFeedId(null);
							}}
						>
							{t(zone)}
						</button>
					))}
				</div>

				<div className={styles.viewSwitch} aria-label={t("Camera grid density")}>
					<button
						type="button"
						className={density === "comfortable" ? styles.activeView : ""}
						onClick={() => setDensity("comfortable")}
						title={t("Comfort view")}
					>
						<span className="material-icons">dashboard</span>
					</button>
					<button
						type="button"
						className={density === "compact" ? styles.activeView : ""}
						onClick={() => setDensity("compact")}
						title={t("Compact view")}
					>
						<span className="material-icons">view_comfy</span>
					</button>
				</div>
			</section>

			<section className={styles.safetyStrip} aria-label={t("CCTV system health")}>
				<div>
					<span className="material-icons">verified_user</span>
					{t("All camera groups armed")}
				</div>
				<div>
					<span className="material-icons">radio_button_checked</span>
					{t("Recording active")}
				</div>
				<div>
					<span className="material-icons">notifications_none</span>
					{t("No active alerts")}
				</div>
			</section>

			<main className={styles.content}>
				{selectedFeed ? (
					<div className={styles.focusLayout}>
						<section className={styles.focusPanel}>
							<div className={styles.focusTitle}>
								<div>
									<span>{t(selectedFeed.zone)}</span>
									<h3>{t(selectedFeed.name)}</h3>
								</div>
								<div className={styles.focusControls}>
									<span>
										<span className="material-icons">radio_button_checked</span>
										{t("Recording")}
									</span>
									<span>
										<span className="material-icons">network_check</span>
										{t(selectedFeed.signal)}
									</span>
									<span>
										<span className="material-icons">motion_photos_on</span>
										{t(selectedFeed.lastMotion)}
									</span>
								</div>
							</div>
							<FeedVisual feed={selectedFeed} large t={t} />
							<div className={styles.focusActionBar}>
								<button type="button">
									<span className="material-icons">photo_camera</span>
									{t("Snapshot")}
								</button>
								<button type="button">
									<span className="material-icons">flag</span>
									{t("Mark incident")}
								</button>
								<button type="button">
									<span className="material-icons">fullscreen</span>
									{t("Fullscreen")}
								</button>
							</div>
						</section>

						<aside className={styles.sideRail} aria-label={t("Other CCTV feeds")}>
							{secondaryFeeds.map((feed) => (
								<button
									key={feed.id}
									type="button"
									className={styles.railFeed}
									onClick={() => setSelectedFeedId(feed.id)}
								>
									<FeedVisual feed={feed} t={t} />
									<span>{t(feed.name)}</span>
								</button>
							))}
						</aside>
					</div>
				) : (
					<div
						className={`${styles.feedGrid} ${
							density === "compact" ? styles.compactGrid : ""
						}`}
					>
						{visibleFeeds.length ? (
							visibleFeeds.map((feed) => (
								<FeedCard
									key={feed.id}
									feed={feed}
									onSelect={setSelectedFeedId}
									t={t}
								/>
							))
						) : (
							<div className={styles.emptyState}>
								<span className="material-icons">videocam_off</span>
								<h3>{t("No cameras found")}</h3>
								<p>{t("Try another zone or search term.")}</p>
							</div>
						)}
					</div>
				)}
			</main>
		</div>
	);
}

export default CCTVCommandCenter;
